import React, { useEffect, useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import toast from 'react-hot-toast'
import { getPortalSocket } from '../utils/portalSocket'
import { chatFocusState } from '../utils/chatFocusState'

const normPhone = (p) => String(p ?? '').trim()

function showIncomingMessageToast({ senderName, line, isGroup, openChat }) {
  const name = String(senderName || 'Someone').trim() || 'Someone'
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0])
    .join('')
    .toUpperCase()
  const body = String(line || '').trim() || '(message)'

  toast.custom(
    (t) => (
      <div className="pointer-events-auto w-full max-w-sm rounded-2xl shadow-xl shadow-emerald-900/10 ring-1 ring-emerald-200/70 bg-white overflow-hidden animate-fade-in-up">
        <div className="relative flex gap-3 p-4 bg-gradient-to-br from-emerald-50 via-white to-teal-50/90">
          <div
            aria-hidden
            className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600"
          />
          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-sm font-black text-white shadow-md ring-2 ring-white">
            {initials || '?'}
          </div>
          <div className="relative min-w-0 flex-1 pt-0.5">
            <p className="text-[13px] leading-tight">
              <span className="font-semibold text-emerald-900">{name}</span>
              {isGroup ? (
                <span className="font-medium text-slate-400"> · group · </span>
              ) : (
                <span className="font-medium text-slate-400"> · </span>
              )}
              <span className="font-semibold text-slate-600">says</span>
            </p>
            <p className="mt-1.5 text-sm text-slate-700 leading-snug line-clamp-3 break-words">
              {body}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-emerald-100 border-t border-emerald-100 bg-emerald-50/70">
          <button
            type="button"
            className="py-3 text-[13px] font-semibold text-slate-600 hover:bg-white/70 transition-colors"
            onClick={() => toast.dismiss(t.id)}
          >
            Dismiss
          </button>
          <button
            type="button"
            className="py-3 text-[13px] font-bold text-emerald-800 hover:bg-emerald-100/70 transition-colors"
            onClick={() => {
              toast.dismiss(t.id)
              openChat()
            }}
          >
            Open chat
          </button>
        </div>
      </div>
    ),
    { duration: 8000 }
  )
}

function dmPreview(data) {
  return String(data.message || '').trim()
}

function groupPreview(data) {
  if (data.messageType === 'file' || data.messageType === 'image') {
    return data.fileName || 'Attachment'
  }
  return String(data.message || '').trim()
}

function shouldSuppressDm(data, selfPhone) {
  if (!data?.sender || normPhone(data.sender) === normPhone(selfPhone)) return true
  if (chatFocusState.view !== 'direct') return false
  return normPhone(chatFocusState.directPeer) === normPhone(data.sender)
}

function shouldSuppressGroup(data, selfPhone) {
  if (normPhone(data.senderPhone) === normPhone(selfPhone)) return true
  if (chatFocusState.view !== 'groups') return false
  return String(chatFocusState.groupId) === String(data.groupId || '')
}

/** Listens on the shared portal socket — works on every dashboard route, not only the Chat tab. */
export default function GlobalChatToasts() {
  const navigate = useNavigate()

  const openChat = useCallback(() => {
    const portal = String(localStorage.getItem('userType') || '').toLowerCase()
    if (portal === 'faculty') navigate('/faculty/chat')
    else if (portal === 'admin') navigate('/admin?page=chat')
    else navigate('/student?page=chat')
  }, [navigate])

  useEffect(() => {
    const token = localStorage.getItem('userToken')
    if (!token) return undefined

    let userData = {}
    try {
      userData = jwtDecode(token)
    } catch {
      return undefined
    }

    const selfPhone = normPhone(userData.phone)
    if (!selfPhone) return undefined

    const socket = getPortalSocket()

    const onConnect = () => {
      socket.emit('join_app', selfPhone)
    }

    const onReceiveDm = (data) => {
      if (shouldSuppressDm(data, selfPhone)) return
      const preview = dmPreview(data)
      const line =
        preview.length > 180 ? `${preview.slice(0, 177)}…` : preview || 'Sent you a message.'
      showIncomingMessageToast({
        senderName: data.senderName || data.sender || 'Contact',
        line,
        isGroup: false,
        openChat,
      })
    }

    const onRecvGroup = (data) => {
      if (shouldSuppressGroup(data, selfPhone)) return
      const raw = groupPreview(data)
      const line = raw.length > 180 ? `${raw.slice(0, 177)}…` : raw || 'Sent a message in the group.'
      showIncomingMessageToast({
        senderName: data.senderName?.trim() || 'Member',
        line,
        isGroup: true,
        openChat,
      })
    }

    socket.on('connect', onConnect)
    socket.on('receive_message', onReceiveDm)
    socket.on('receive_group_message', onRecvGroup)
    socket.emit('join_app', selfPhone)

    return () => {
      socket.off('connect', onConnect)
      socket.off('receive_message', onReceiveDm)
      socket.off('receive_group_message', onRecvGroup)
    }
  }, [openChat])

  return null
}

/** Tracks token-only routes — remount hooks when navigating after login/logout. */
export function PortalGlobalToastsGate() {
  const location = useLocation()
  const [hasToken, setHasToken] = useState(
    () => !!localStorage.getItem('userToken')
  )

  useEffect(() => {
    setHasToken(!!localStorage.getItem('userToken'))
  }, [location.pathname])

  if (!hasToken) return null
  return <GlobalChatToasts />
}
